import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Boxes, Plus, Search, X } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { tiposDe } from '../../componentes/formatos';
import { FormAtivo } from './FormAtivo';
import { ArvoreAtivos, type ItemArvore } from './ArvoreAtivos';
import { NOME_PROPRIEDADE, NOME_STATUS_ATIVO, type Categoria } from './comum';

interface Item extends ItemArvore { agrupador?: boolean; apelido: string; status: string; propriedade: string; numero_serie: string | null; ip: string | null; fabricante: string | null; modelo: string | null; categoria: string; local_id: string; local: string; cliente_id: string; cliente: string }

export function ListaAtivos() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState(params.get('busca') ?? '');
  const [dados, setDados] = useState<{ total: number; por_pagina: number; itens: Item[] } | null>(null);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const podeEditar = pode(usuario, 'cadastro.editar');
  const pagina = Number(params.get('pagina') ?? 1);
  // cliente/parque escolhido e sem outro filtro: árvore Parque → Via → equipamento
  const arvore = !!(params.get('local_id') || params.get('cliente_id')) && !['busca', 'categoria_id', 'status', 'propriedade'].some((k) => params.get(k)) && params.get('modo') !== 'tabela';
  const [recarga, setRecarga] = useState(0);

  useEffect(() => { api<Categoria[]>('/categorias-ativo').then(setCategorias).catch(() => {}); }, []);
  useEffect(() => {
    setErro(null);
    const p = new URLSearchParams(params); p.delete('modo'); if (arvore) { p.set('por_pagina', '500'); p.delete('pagina'); }
    api<typeof dados>(`/ativos?${p}`).then(setDados).catch((e) => setErro(e.message));
  }, [params, arvore, recarga]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('busca') ?? '') === busca) return;
      const p = new URLSearchParams(params); busca ? p.set('busca', busca) : p.delete('busca'); p.delete('pagina'); setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtro = (k: string, v: string) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); p.delete('pagina'); setParams(p); };
  const filtroLocal = params.get('local_id') && dados?.itens[0];
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / dados.por_pagina)) : 1;

  return (
    <>
      <div className="cabecalho-pagina">
        <div><span className="kicker">Cadastro</span><h1 className="titulo">Parque instalado</h1></div>
        {podeEditar && <button className="btn" onClick={() => setNovo(true)}><Plus size={18} strokeWidth={2} aria-hidden /> Novo equipamento</button>}
      </div>
      <div className="barra-filtros">
        <label className="campo-busca"><Search size={18} strokeWidth={1.7} aria-hidden />
          <input type="search" placeholder="Nome, fabricante, modelo, nº de série, IP ou código da etiqueta" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar equipamentos" />
        </label>
        <select className="filtro" value={params.get('categoria_id') ?? ''} onChange={(e) => filtro('categoria_id', e.target.value)} aria-label="Categoria">
          <option value="">Todas as categorias</option>
          {categorias.filter((c) => !c.pai_id).map((r) => (
            <optgroup key={r.id} label={r.nome}><option value={r.id}>{r.nome} (todas)</option>
              {categorias.filter((c) => c.pai_id === r.id).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</optgroup>))}
        </select>
        <select className="filtro" value={params.get('status') ?? ''} onChange={(e) => filtro('status', e.target.value)} aria-label="Situação">
          <option value="">Situação (menos removidos)</option>
          {tiposDe(NOME_STATUS_ATIVO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
        </select>
        <select className="filtro" value={params.get('propriedade') ?? ''} onChange={(e) => filtro('propriedade', e.target.value)} aria-label="Propriedade">
          <option value="">De qualquer dono</option>
          {tiposDe(NOME_PROPRIEDADE).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
        </select>
      </div>
      {(params.get('local_id') || params.get('cliente_id')) && (
        <p className="filtro-ativo">Filtrando por {filtroLocal ? <strong>{filtroLocal.cliente} · {filtroLocal.local}</strong> : 'cliente/parque'}
          <button className="btn-link" onClick={() => { const p = new URLSearchParams(params); p.delete('local_id'); p.delete('cliente_id'); p.delete('modo'); setParams(p); }}><X size={14} aria-hidden /> limpar</button>
          <button className="btn-link" onClick={() => filtro('modo', params.get('modo') === 'tabela' ? '' : 'tabela')}>{params.get('modo') === 'tabela' ? 'ver em árvore' : 'ver em tabela'}</button></p>
      )}
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {dados && dados.itens.length === 0 && (
        <div className="cartao vazio"><div className="icone-tile"><Boxes size={24} strokeWidth={1.7} aria-hidden /></div>
          <p>{params.toString() ? 'Nenhum equipamento com esse filtro.' : 'Nenhum equipamento cadastrado ainda. Comece pelos de maior chamado: motores de portão, DVRs e leitores.'}</p></div>
      )}
      {arvore && dados && dados.itens.length > 0 && <ArvoreAtivos key={params.toString()} itens={dados.itens} podeEditar={podeEditar} aoMudar={() => setRecarga((n) => n + 1)} />}
      {!arvore && dados && dados.itens.length > 0 && (
        <div className="cartao tabela-cartao">
          <table className="tabela tabela-ativos">
            <thead><tr><th>Equipamento</th><th>Categoria</th><th>Cliente · parque</th><th>Situação</th><th>Dono</th></tr></thead>
            <tbody>{dados.itens.map((a) => (
              <tr key={a.id} onClick={() => navegar(`/ativos/${a.id}`)}>
                <td><Link to={`/ativos/${a.id}`} onClick={(e) => e.stopPropagation()}><strong>{a.apelido}</strong></Link>{a.agrupador && <span className="tag-agrupador">via/setor</span>}
                  {!a.agrupador && <small>{[a.fabricante, a.modelo].filter(Boolean).join(' ') || 'modelo não informado'}{a.ip ? ` · ${a.ip}` : ''}</small>}</td>
                <td>{a.categoria}</td>
                <td>{a.cliente}<small>{a.local}</small></td>
                <td><span className={`status ativo-${a.status}`}>{NOME_STATUS_ATIVO[a.status]}</span></td>
                <td>{a.propriedade === 'cliente' ? <span className="nota">Cliente</span> : <span className="role-maza">{NOME_PROPRIEDADE[a.propriedade]}</span>}</td>
              </tr>))}</tbody>
          </table>
          <footer className="rodape-tabela"><span>{dados.total} equipamento{dados.total === 1 ? '' : 's'}</span>
            {totalPaginas > 1 && <span className="paginacao">
              <button className="btn-fantasma" disabled={pagina <= 1} onClick={() => filtro('pagina', String(pagina - 1))}>Anterior</button>{pagina} de {totalPaginas}
              <button className="btn-fantasma" disabled={pagina >= totalPaginas} onClick={() => filtro('pagina', String(pagina + 1))}>Próxima</button></span>}
          </footer>
        </div>
      )}
      <Modal titulo="Novo equipamento" aberto={novo} aoFechar={() => setNovo(false)}>
        <FormAtivo aoSalvar={(id) => { setNovo(false); navegar(`/ativos/${id}`); }} aoCancelar={() => setNovo(false)} />
      </Modal>
    </>
  );
}
